import 'react-day-picker/lib/style.css';

import React from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import Helmet from 'react-helmet';
import Select from 'react-select';

import {translate} from 'iss/strings';
import {addTime, modifyTime, removeTime, visibilityTimes} from 'iss/service/FiilissVisibilityService';
import {Button, RemoveButton, SubmitButton} from 'iss/widgets/Buttons';
import {Panel} from 'iss/widgets/Panels';
import dates from 'iss/utils/dates';

function FiilissVisibilityTimes() {
    const now = new Date();
    const yearFromNow = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
    return <>
        <h1>{translate('fiilissVisibility.title')}</h1>
        <VisibilityTimeEditor
            years={[
                now.getFullYear(),
                yearFromNow.getFullYear()
            ]}
            />
    </>;
}

function Instructions({isExisting, year, from, to, isDirty}) {
    return <>
        { isExisting ? null : <h4>{translate('fiilissVisibility.noExistingQuestionnaire')}</h4>}
        <h4>
            {!year && translate('fiilissVisibility.selectYear')}
            {year && !from && !to && translate('fiilissVisibility.selectStartDate')}
            {from && !to && translate('fiilissVisibility.selectEndDate')}
            {isDirty && from && to &&
                `${translate('fiilissVisibility.selectedRange')}:
                ${from.toLocaleDateString('fi-FI')} - ${to.toLocaleDateString('fi-FI')}`}{' '}
            {!isDirty && isExisting && from && to &&
                `${translate('fiilissVisibility.existingRange')}:
                ${from.toLocaleDateString('fi-FI')} - ${to.toLocaleDateString('fi-FI')}`}{' '}
        </h4>
    </>;
}

class VisibilityTimeEditor extends React.Component {
    state = {
        existingTimesAll: [],
        existingTime: null,
        selectedYear: null,
        from: undefined,
        to: undefined,
        selectionDirty: false,
        yearOptions: this.props.years,
    };

    subscription = null;

    componentDidMount() {
        this.subscription = this.subscribe();
    }

    componentWillUnmount() {
        this.subscription.unsubscribe();
    }

    subscribe() {
        return visibilityTimes.subscribe(
            times => this.processResponse(times)
        );
    }

    processResponse = (existingVisibilityTimes) => {
        const reverseSortedTimes = existingVisibilityTimes.sort((a, b) => {
            if (a.startDate > b.startDate
                || (a.startDate === b.startDate && a.endDate > b.endDate)) {
                return -1;
            }
            if (a.startDate < b.startDate) {
                return 1;
            }
            return 0;
        });

        let newState = {
            existingTimesAll: reverseSortedTimes,
        };

        if (reverseSortedTimes.length > 0) {
            const latestStartDate = new Date(reverseSortedTimes[0].startDate);
            const latestYear = latestStartDate.getFullYear();

            this.selectVisibilityTimeIfExists(existingVisibilityTimes, latestYear);
            newState = {
                ...newState,
                selectedYear: latestYear,
                yearOptions: [latestYear],
            };
        }

        this.setState(newState);
    };

    handleSelection = (selectedOption) => {
        if (selectedOption) {
            const arrayContainsSelection = (array) => {
                return array.filter(e => e == selectedOption.value).length > 0;
            };
            if (arrayContainsSelection(this.props.years)) {
                const selectedYear = selectedOption.value;
                this.setState({from: undefined, to: undefined, existingTime: null, selectedYear: selectedYear});
                this.selectVisibilityTimeIfExists(this.state.existingTimesAll, selectedYear);
            }
        }
    };

    resetDateSelection = () => {
        this.setState({from: undefined, to: undefined, existingTime: null});
    };

    resetAll = () => {
        this.setState({
            existingTime: null,
            selectedYear: null,
            from: undefined,
            to: undefined,
            yearOptions: this.props.years,
        });
    };

    getDateFromText = (text) => {
        const splitText = text.split("-");
        return new Date(splitText[0], splitText[1] - 1, splitText[2]);
    };

    selectVisibilityTimeIfExists = (existingVisibilityTimes, selectedYear) => {
        if (selectedYear !== null) {
            const existingTimes = existingVisibilityTimes.filter(t =>
                t.startDate.split('-')[0] == selectedYear
            );
            if (existingTimes.length > 0) {
                const start = this.getDateFromText(existingTimes[0].startDate);
                const end = this.getDateFromText(existingTimes[0].endDate);
                this.setState({
                        existingTime: existingTimes[0],
                        from: start,
                        to: end
                });
            }
        }
    };

    handleDayClick = (day) => {
        this.setState({selectionDirty: true});
        if (this.state.from === undefined || this.state.to === undefined) {
            const range = DateUtils.addDayToRange(day, this.state);
            this.setState({from: range.from, to: range.to});
        }
    };

    handleResetClick = () => {this.setState({from: undefined, to: undefined});};

    onSaveCompleted = () => {
        this.subscription.unsubscribe();
        this.subscribe();
        this.setState({selectionDirty: false});
    };

    onDeleteCompleted = () => {
        this.onSaveCompleted();
        this.resetAll();
    };

    onError = (error) => console.error('Caught error:', error);

    submitTime = () => {
        const payload = {
            'startDate': dates.formatUTC(this.state.from),
            'endDate': dates.formatUTC(this.state.to)
        };
        if (this.state.existingTime == null) {
            addTime(payload).subscribe(this.onSaveCompleted, this.onError);
        } else {
            modifyTime(this.state.existingTime.id, payload).subscribe(this.onSaveCompleted, this.onError);
        }
    };

    deleteTime = () => {
        removeTime(this.state.existingTime.id).subscribe(this.onDeleteCompleted, this.onError);
    };

    getInitialDate = (existingTime, selectedYear) => {
        if (existingTime !== null) {
            return dates.getDateClosestToNow(this.getDateFromText(existingTime.startDate), this.getDateFromText(existingTime.endDate));
        }
        const now = new Date();
        if (selectedYear > now.getFullYear()) {
            return new Date(selectedYear, 0);
        }
        return new Date(selectedYear, now.getMonth());
    };

    render() {
        const {
            selectedYear,
            from,
            to,
            existingTime,
            selectionDirty
        } = this.state;
        const modifiers = { start: from, end: to };
        const yearOptions = this.state.yearOptions.map(opt => ({ value: opt, label: opt }));
        const initMonth = this.getInitialDate(existingTime, selectedYear);
        const selectorDivStyle = {display: 'flex', alignItems: 'center'};
        const selectStyle = {margin: '10px 10px 10px 10px', width: '150px'};
        const marginStyle = {margin: '10px'};
        return <>
            <Panel panelType={'default'}>
                <Instructions isExisting={!!existingTime} year={selectedYear} from={from} to={to} isDirty={selectionDirty}/>
                <div style={selectorDivStyle}>
                    <Select
                        id="yearSelect"
                        classNamePrefix="yearSelect"
                        options={yearOptions}
                        onChange={this.handleSelection}
                        value={selectedYear}
                        style={selectStyle}
                        placeholder={translate('fiilissVisibility.year')}
                    />
                </div>
                {selectedYear !== null && (
                    <div >
                        <DayPicker
                            className="Selectable"
                            numberOfMonths={1}
                            month={initMonth}
                            fromMonth={new Date(selectedYear, 0)}
                            toMonth={new Date(selectedYear, 11)}
                            selectedDays={[to, {to, from}]}
                            modifiers={modifiers}
                            onDayClick={this.handleDayClick}
                            locale="fi"
                        />
                        <Helmet>
                            <style>
                                {`
                                .Selectable .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside) {
                                    background-color: #f0f8ff !important;
                                    color: #4a90e2;
                                }
                                .Selectable .DayPicker-Day {
                                    border-radius: 0 !important;
                                }
                                .Selectable .DayPicker-Day--start {
                                    border-top-left-radius: 50% !important;
                                    border-bottom-left-radius: 50% !important;
                                }
                                .Selectable .DayPicker-Day--end {
                                    border-top-right-radius: 50% !important;
                                    border-bottom-right-radius: 50% !important;
                                }
                                .DayPicker {
                                    font-size: 1.5rem;
                                }
                                `}
                            </style>
                        </Helmet>
                    </div>
                )}
                {from && to && (
                    <Button text={translate('fiilissVisibility.reset')} onClick={this.handleResetClick} style={marginStyle}/>
                )}
                <div style={marginStyle}>
                    <SubmitButton
                        text={translate(`fiilissVisibility.${existingTime !== null ? 'update' : 'save'}`)}
                        onClick={this.submitTime}
                        disabled={(!selectedYear || !from || !to)}
                        />
                    { existingTime !== null &&
                        <RemoveButton text={translate('fiilissVisibility.delete')} onClick={this.deleteTime} />
                    }
                </div>
            </Panel>
         </>;
    }
}

export default FiilissVisibilityTimes;


// WEBPACK FOOTER //
// ./modules/issFrontend/pages/FiilissVisibilityTimes.jsx